import { useContext, useEffect, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  enUS as en,
  esES as es,
  ptBR,
} from "@mui/x-data-grid";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { AuthContext } from "../../contexts/AuthContext";
import { CONFIG } from "../../services/config";
import { FormInputText } from "../../components/form/FormInputText";
import MyModal from "../../components/MyModal";

type Props = {
  campaign: any;
  onClose: Function;
};

interface ErrorRow {
  id: string;
  description: string;
}

const DEFAULT_PAGE_SIZE = 25;

const getCampaignId = (campaign: any) =>
  campaign?.id || campaign?._id || campaign?.campaignId;

export default function ModalViewErrors({ campaign, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const {
    tokenHeaders,
    country: { countryCode },
  } = useContext(AuthContext);

  const campaignId = getCampaignId(campaign);

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [allRows, setAllRows] = useState<ErrorRow[]>([]);
  const [rows, setRows] = useState<ErrorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const localeByLanguage: { [language: string]: any } = {
    ptbr: ptBR,
    en,
    es,
  };

  const localeText =
    localeByLanguage[i18n.language]?.components?.MuiDataGrid?.defaultProps
      ?.localeText ||
    localeByLanguage.ptbr.components.MuiDataGrid.defaultProps.localeText;

  const columns: GridColDef[] = [
    {
      field: "description",
      headerName: t("ottoTrialPeriod.errors.columns.errorDescription"),
      flex: 1,
      minWidth: 220,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "header-style",
    },
  ];

  const applyLocalPagination = (
    sourceRows: ErrorRow[],
    filterValue: string,
    nextPage: number,
    nextSize: number,
  ) => {
    const normalizedFilter = filterValue.trim().toLowerCase();
    const filtered = sourceRows.filter((row) =>
      row.description.toLowerCase().includes(normalizedFilter),
    );
    const start = nextPage * nextSize;
    const paged = filtered.slice(start, start + nextSize);

    setRows(paged);
    setTotal(filtered.length);
  };

  const fetchErrors = async () => {
    if (!campaignId) return;

    setLoading(true);
    setErrorMessage("");

    const requestHeaders = {
      ...tokenHeaders.headers,
      countryCode: countryCode || tokenHeaders.headers.countryCode,
      countrycode: countryCode || tokenHeaders.headers.countryCode,
      "Content-Type": "application/json",
    };

    try {
      const response = await axios.get(
        `${CONFIG.API_URL}temporaryAccess/${campaignId}/errors`,
        {
          headers: requestHeaders,
          params: { page: 0, size: 1000 },
        },
      );

      const content: any[] = response.data?.content || [];
      const fetchedRows: ErrorRow[] = content.map(
        (item: any, index: number) => ({
          id: `${item.rowNumber}-${item.errorCode}-${index}`,
          description: `Problema na linha '${item.rowNumber}': ${item.errorMessage}`,
        }),
      );

      setAllRows(fetchedRows);
      applyLocalPagination(fetchedRows, "", 0, DEFAULT_PAGE_SIZE);
    } catch (error: any) {
      setAllRows([]);
      setRows([]);
      setTotal(0);
      setErrorMessage(
        error?.response?.data?.message ||
          t("ottoTrialPeriod.errors.errorLoad"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setAppliedSearch(searchValue);
    applyLocalPagination(allRows, searchValue, 0, pageSize);
  };

  const handleClear = () => {
    setSearchValue("");
    setAppliedSearch("");
    setPage(0);
    applyLocalPagination(allRows, "", 0, pageSize);
  };

  useEffect(() => {
    fetchErrors();
  }, [campaignId]);

  return (
    <MyModal
      title={t("ottoTrialPeriod.errors.title")}
      modalChange={onClose}
      width="70rem"
      maxWidth="70rem"
      disableDivider
    >
      <Box sx={{ borderTop: "1px solid var(--gray-100)", mt: 1.5, pt: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-start" },
            gap: { xs: 1.5, sm: 2.5 },
            marginBottom: 2,
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: 300 } }}>
            <FormInputText
              id="viewErrorsSearch"
              type="text"
              label={t("ottoTrialPeriod.errors.searchInputText")}
              value={searchValue}
              onChange={(event: any) => setSearchValue(event.target.value)}
              onKeyDown={(event: any) => {
                if (event.key === "Enter") {
                  handleSearch();
                }
              }}
              sx={{ marginBottom: 0 }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: { xs: "100%", sm: "auto" },
              "& .MuiButton-root": {
                height: 40,
                minWidth: 116,
              },
            }}
          >
            <Button variant="contained" onClick={handleSearch}>
              {t("button.Search")}
            </Button>
            <Button
              sx={{ color: "#00AFF2" }}
              variant="outlined"
              onClick={handleClear}
            >
              {t("ottoTrialPeriod.list.cleanButtonText")}
            </Button>
          </Box>
        </Box>

        {errorMessage && (
          <Box sx={{ marginBottom: 2 }}>
            <Alert severity="error">{errorMessage}</Alert>
            <Alert severity="warning" sx={{ marginTop: 1 }}>
              {t("alert.tryAgain")}
            </Alert>
          </Box>
        )}

        <Box
          sx={{
            width: "100%",
            "& .header-style": {
              backgroundColor: "var(--gray-100)",
              minHeight: "4rem",
            },
          }}
        >
          <DataGrid
            rowCount={total}
            localeText={{
              ...localeText,
              noRowsLabel: t("ottoTrialPeriod.errors.emptyMessage"),
            }}
            getRowId={(row) => row.id}
            rowHeight={52}
            getRowHeight={() => "auto"}
            rows={rows}
            columns={columns}
            loading={loading}
            rowsPerPageOptions={[25, 50, 75, 100]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode="server"
            onPageChange={(newPage) => {
              setPage(newPage);
              applyLocalPagination(allRows, appliedSearch, newPage, pageSize);
            }}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
              applyLocalPagination(allRows, appliedSearch, 0, newPageSize);
            }}
            autoHeight
            disableSelectionOnClick
            sx={{
              cursor: "default",
              "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                py: 1,
              },
              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "15px",
              },
              "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                py: "22px",
              },
            }}
            sortingOrder={["asc", "desc"]}
          />
        </Box>
      </Box>
    </MyModal>
  );
}
